// @flow
import * as React from 'react';

import { selectCartCurrency } from '@gandi/cart';
import { selectCountryList } from '@gandi/redux-places';
import { selectCurrentId } from '@gandi/redux-sharing';

import { useQuery } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  selectIsFreshUser,
  selectCountry,
  selectCurrency,
  selectIsAnonymous,
  selectTaxesIncluded,
} from '../modules/selectors';

export type Query = {
  org?: string,
  country?: string,
  currency?: string,
  taxes?: string,
};

type Preferences = {
  currency: string,
  country?: string,
  taxesIncluded: boolean,
};

/**
 * Retrieves user preferences, this hook is heavily based on querystring cf. this Query type
 */
export function usePreferences(): void | Preferences {
  const currentId = useAppSelector(selectCurrentId);
  const query = useQuery<Query>();
  const orgId = query.org || currentId;

  const countryCode = useAppSelector(state =>
    selectCountry(state, {
      locationQueryCountry: query.country,
      orgId,
    }),
  );

  const countryList = useAppSelector(selectCountryList);
  const country = countryList.find(c => c.alpha2 === countryCode);

  const currency = useAppSelector(state =>
    selectCurrency(state, {
      locationQueryCurrency: query.currency,
      country: countryCode,
      orgId,
    }),
  );

  const cartCurrency = useAppSelector(selectCartCurrency);

  const currencyConflict = cartCurrency && cartCurrency !== currency;

  const taxesIncluded = useAppSelector(state =>
    selectTaxesIncluded(state, query.taxes),
  );

  // preferences
  // set preferences for fresh user and anonymous
  const isAnonymous = useAppSelector(selectIsAnonymous);
  const isFreshUser = useAppSelector(state => selectIsFreshUser(state, orgId));
  const shouldSetPreferences = isFreshUser || isAnonymous;

  const preferences = React.useMemo(
    () =>
      shouldSetPreferences
        ? {
            currency: cartCurrency || currency,
            country: currencyConflict ? undefined : country?.name,
            taxesIncluded,
          }
        : undefined,
    [
      currency,
      country,
      taxesIncluded,
      shouldSetPreferences,
      cartCurrency,
      currencyConflict,
    ],
  );

  return preferences;
}
